/**
 * Align + distribute — pure geometry producing `{ id, next }` patch entries
 * that feed `makeTransformCommand`, so a whole align/distribute is a single
 * undo step. Rotated objects align by their world AABB (industry standard).
 *
 * @module floorplan/snapping/align
 * @category FloorPlan
 */

import type { FloorObject } from '../models/object';
import { objectAabb } from '../utils/bounds';

export type AlignEdge =
  'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

export type AlignPatch = {
  id: string;
  next: { x: number } | { y: number };
};

type Aabbed = { object: FloorObject; aabb: ReturnType<typeof objectAabb> };

const collectAabbs = (objects: readonly FloorObject[]): Aabbed[] => {
  const result: Aabbed[] = [];
  for (const object of objects) {
    if (object.locked) continue;
    result.push({ object, aabb: objectAabb(object) });
  }
  return result;
};

/**
 * Align ≥2 objects on an edge/center of the selection's union bounds.
 * Returns patch entries (unchanged objects omitted).
 */
export const alignObjects = (
  objects: readonly FloorObject[],
  edge: AlignEdge,
): AlignPatch[] => {
  const entries = collectAabbs(objects);
  if (entries.length < 2) return [];

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const entry of entries) {
    minX = Math.min(minX, entry.aabb.x);
    maxX = Math.max(maxX, entry.aabb.x + entry.aabb.width);
    minY = Math.min(minY, entry.aabb.y);
    maxY = Math.max(maxY, entry.aabb.y + entry.aabb.height);
  }
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const patches: AlignPatch[] = [];
  for (const { object, aabb } of entries) {
    // Delta of the object's AABB → applied to object.x/y (AABB moves 1:1).
    let dx = 0;
    let dy = 0;
    if (edge === 'left') dx = minX - aabb.x;
    if (edge === 'right') dx = maxX - (aabb.x + aabb.width);
    if (edge === 'centerX') dx = centerX - (aabb.x + aabb.width / 2);
    if (edge === 'top') dy = minY - aabb.y;
    if (edge === 'bottom') dy = maxY - (aabb.y + aabb.height);
    if (edge === 'centerY') dy = centerY - (aabb.y + aabb.height / 2);
    if (dx !== 0) patches.push({ id: object.id, next: { x: object.x + dx } });
    if (dy !== 0) patches.push({ id: object.id, next: { y: object.y + dy } });
  }
  return patches;
};

/**
 * Distribute ≥3 objects with equal gaps between their AABBs along an axis.
 * First and last (by position) stay fixed; the middle spreads evenly.
 */
export const distributeObjects = (
  objects: readonly FloorObject[],
  axis: 'horizontal' | 'vertical',
): AlignPatch[] => {
  const entries = collectAabbs(objects);
  if (entries.length < 3) return [];

  const isH = axis === 'horizontal';
  const sorted = [...entries].sort((a, b) =>
    isH ? a.aabb.x - b.aabb.x : a.aabb.y - b.aabb.y,
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === undefined || last === undefined) return [];

  const start = isH ? first.aabb.x : first.aabb.y;
  const end = isH
    ? last.aabb.x + last.aabb.width
    : last.aabb.y + last.aabb.height;
  let totalSize = 0;
  for (const entry of sorted) {
    totalSize += isH ? entry.aabb.width : entry.aabb.height;
  }
  const gap = (end - start - totalSize) / (sorted.length - 1);

  const patches: AlignPatch[] = [];
  let cursor = start;
  for (let i = 0; i < sorted.length; i += 1) {
    const entry = sorted[i];
    if (entry === undefined) continue;
    const size = isH ? entry.aabb.width : entry.aabb.height;
    // First and last already sit at their targets by construction.
    if (i > 0 && i < sorted.length - 1) {
      const current = isH ? entry.aabb.x : entry.aabb.y;
      const delta = cursor - current;
      if (delta !== 0) {
        patches.push(
          isH
            ? { id: entry.object.id, next: { x: entry.object.x + delta } }
            : { id: entry.object.id, next: { y: entry.object.y + delta } },
        );
      }
    }
    cursor += size + gap;
  }
  return patches;
};
